"use client"

import { useState, useRef } from "react"
import {
  useForm,
  FormProvider,
  useFormContext,
  Controller,
} from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Eye, EyeOff, Loader2, Mail, RefreshCw, ArrowLeft, CheckCircle2 } from "lucide-react"
import { useTranslations } from "next-intl"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { useAuthStore } from "@/hooks/use-auth"
import { useAuthModalStore } from "@/hooks/use-auth-modal"
import { useApiError } from "@/hooks/use-api-error"
import { forgotPasswordApi } from "@/lib/api/auth"
import { strongPasswordSchema } from "@/lib/schemas/password"
import { personNameSchema } from "@/lib/schemas/person-name"
import { isValidPhoneNumber } from "@/components/rhf/RHFPhoneInput"
import PhoneInput from "react-phone-number-input"
import flags from "react-phone-number-input/flags"
import "react-phone-number-input/style.css"

// ── Zod schemas ───────────────────────────────────────────────────────────────

const loginSchema = z.object({
  email: z.string().min(1, "email is required").email("email is required"),
  password: z.string().min(1, "password is required"),
})

const registerSchema = z
  .object({
    name: personNameSchema,
    email: z.string().min(1, "email is required").email("email is required"),
    phone: z
      .string()
      .optional()
      .refine(
        (v) => !v || isValidPhoneNumber(v),
        "Please enter a valid phone number"
      ),
    password: strongPasswordSchema,
    confirmPassword: z.string().min(1, "confirm password is required"),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "email is required").email("email is required"),
})

type LoginValues = z.infer<typeof loginSchema>
type RegisterValues = z.infer<typeof registerSchema>
type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>

// ── TextField (register-based, works reliably inside Dialog) ────────────────

function TextField({
  name,
  label,
  type = "text",
  placeholder,
  autoComplete,
}: {
  name: keyof RegisterValues | keyof LoginValues
  label: string
  type?: "text" | "email"
  placeholder?: string
  autoComplete?: string
}) {
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const err = errors[name]

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        {...register(name)}
        className={cn(
          "h-11 w-full rounded-lg border bg-card px-3 text-sm text-card-foreground transition-colors outline-none placeholder:text-muted-foreground",
          err
            ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
            : "border-border focus:border-primary focus:ring-2 focus:ring-primary/20"
        )}
      />
      {err && <p className="text-xs text-red-500">{err.message as string}</p>}
    </div>
  )
}

// ── PhoneField (Controller-based, optional) ───────────────────────────────────

function PhoneField({
  name,
  label,
  optionalLabel,
}: {
  name: keyof RegisterValues
  label: string
  optionalLabel: string
}) {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const err = errors[name]

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-foreground">
        {label}{" "}
        <span className="text-xs font-normal text-muted-foreground">
          ({optionalLabel})
        </span>
      </label>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <PhoneInput
            international
            flags={flags}
            defaultCountry="AE"
            value={(field.value as string) || undefined}
            onChange={(v) => field.onChange(v || "")}
            className={cn(
              "rhf-phone-input w-full rounded-lg border bg-card px-3 py-2.5 text-sm text-card-foreground",
              "dark:border-slate-700 dark:bg-slate-900 dark:text-white",
              err
                ? "border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20"
                : "border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
            )}
          />
        )}
      />
      {err && <p className="text-xs text-red-500">{err.message as string}</p>}
    </div>
  )
}

// ── Password field (RHF via useFormContext + eye toggle) ─────────────────────

function PasswordField({
  name,
  label,
  placeholder,
  autoComplete,
}: {
  name: keyof RegisterValues | keyof LoginValues
  label: string
  placeholder?: string
  autoComplete?: string
}) {
  const [show, setShow] = useState(false)
  const {
    register,
    formState: { errors },
  } = useFormContext()
  const err = errors[name]

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          autoComplete={autoComplete}
          placeholder={placeholder ?? "Enter Password"}
          {...register(name)}
          className={cn(
            "h-11 w-full rounded-lg border bg-card px-3 pe-10 text-sm text-card-foreground transition-colors outline-none placeholder:text-muted-foreground",
            err
              ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
              : "border-border focus:border-primary focus:ring-2 focus:ring-primary/20"
          )}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute inset-y-0 end-0 flex items-center pe-3 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {err && <p className="text-xs text-red-500">{err.message as string}</p>}
    </div>
  )
}

// ── OTP input component ──────────────────────────────────────────────────────

function OtpInput({
  value,
  onChange,
  disabled,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
}) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Always a 6-element array of single chars or ""
  const digits: string[] = Array.from({ length: 6 }, (_, i) => value[i] ?? "")

  function commit(next: string[]) {
    onChange(next.join(""))
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number
  ) {
    if (e.key === "Backspace") {
      e.preventDefault()
      if (digits[idx] !== "") {
        const next = [...digits]
        next[idx] = ""
        commit(next)
      } else if (idx > 0) {
        const next = [...digits]
        next[idx - 1] = ""
        commit(next)
        inputRefs.current[idx - 1]?.focus()
      }
    } else if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault()
      inputRefs.current[idx - 1]?.focus()
    } else if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault()
      inputRefs.current[idx + 1]?.focus()
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, idx: number) {
    const raw = e.target.value.replace(/\D/g, "")
    if (!raw) return
    if (raw.length >= 6) {
      // full paste via input event
      const next = Array.from({ length: 6 }, (_, i) => raw[i] ?? "")
      commit(next)
      inputRefs.current[5]?.focus()
      return
    }
    const char = raw[raw.length - 1]
    const next = [...digits]
    next[idx] = char
    commit(next)
    if (idx < 5) inputRefs.current[idx + 1]?.focus()
  }

  function handlePaste(e: React.ClipboardEvent) {
    e.preventDefault()
    const raw = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6)
    const next = Array.from({ length: 6 }, (_, i) => raw[i] ?? "")
    commit(next)
    const focusIdx = Math.min(raw.length, 5)
    inputRefs.current[focusIdx]?.focus()
  }

  return (
    <div className="flex gap-2">
      {digits.map((digit, idx) => (
        <input
          key={idx}
          ref={(el) => {
            inputRefs.current[idx] = el
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          value={digit}
          onChange={(e) => handleChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          onPaste={handlePaste}
          className={cn(
            "otp-digit-input",
            "h-11 w-full rounded-lg border text-center text-lg font-semibold transition-colors outline-none",
            "border-slate-300 dark:border-slate-600",
            "focus:border-primary focus:ring-2 focus:ring-primary/20",
            disabled && "cursor-not-allowed opacity-50"
          )}
        />
      ))}
    </div>
  )
}

// ── main modal ───────────────────────────────────────────────────────────────

export function AuthModal() {
  const t = useTranslations("auth")
  const tNav = useTranslations("nav")

  const { isOpen, mode, closeModal, switchMode, executePendingAction } =
    useAuthModalStore()
  const login = useAuthStore((s) => s.login)
  const register = useAuthStore((s) => s.register)
  const resendVerification = useAuthStore((s) => s.resendVerification)
  const verifyEmail = useAuthStore((s) => s.verifyEmail)
  const clearError = useAuthStore((s) => s.clearError)

  const { handleErrorSilent, isErrorCode, getFieldErrors } = useApiError({
    showToast: false,
  })

  // ── RHF forms ─────────────────────────────────────────────────────────
  const loginForm = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  })

  const registerForm = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",
    },
  })

  // ── Forgot password form ───────────────────────────────────────────────
  const forgotPasswordForm = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  })

  const [forgotPasswordSent, setForgotPasswordSent] = useState(false)
  const [forgotPasswordError, setForgotPasswordError] = useState("")

  async function onForgotPasswordSubmit(values: ForgotPasswordValues) {
    setForgotPasswordError("")
    try {
      await forgotPasswordApi(values.email)
      setForgotPasswordSent(true)
    } catch (err: unknown) {
      const { message } = handleErrorSilent(err)
      setForgotPasswordError(message)
    }
  }

  // ── OTP panel state ───────────────────────────────────────────────────
  // showOtp = true → OTP panel is shown instead of login/register form
  const [showOtp, setShowOtp] = useState(false)
  const [otpEmail, setOtpEmail] = useState("") // email for the OTP panel
  const [otpOrigin, setOtpOrigin] = useState<"register" | "login">("register")
  const [otpValue, setOtpValue] = useState("")
  const [isSendingOtp, setIsSendingOtp] = useState(false)
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [otpError, setOtpError] = useState("")

  // ── helpers ───────────────────────────────────────────────────────────
  function openOtpPanel(email: string, origin: "register" | "login") {
    setOtpEmail(email)
    setOtpOrigin(origin)
    setOtpValue("")
    setOtpError("")
    setShowOtp(true)
  }

  function closeOtpPanel() {
    setShowOtp(false)
    setOtpValue("")
    setOtpError("")
  }

  function handleClose() {
    clearError()
    loginForm.reset()
    registerForm.reset()
    forgotPasswordForm.reset()
    setForgotPasswordSent(false)
    setForgotPasswordError("")
    closeOtpPanel()
    closeModal()
  }

  // ── send / resend OTP ─────────────────────────────────────────────────
  async function handleSendOtp(email: string) {
    setIsSendingOtp(true)
    setOtpError("")
    try {
      await resendVerification(email)
    } catch (err: unknown) {
      const { message } = handleErrorSilent(err)
      setOtpError(message)
    } finally {
      setIsSendingOtp(false)
    }
  }

  // ── verify OTP ────────────────────────────────────────────────────────
  async function handleVerifyOtp() {
    if (otpValue.replace(/\D/g, "").length < 6) {
      setOtpError("Please enter the full 6-digit code.")
      return
    }
    setIsVerifyingOtp(true)
    setOtpError("")
    try {
      await verifyEmail(otpEmail, otpValue.replace(/\D/g, ""))
      executePendingAction()
    } catch (err: unknown) {
      const { message } = handleErrorSilent(err)
      setOtpError(message)
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  // ── login submit ──────────────────────────────────────────────────────
  async function onLoginSubmit(values: LoginValues) {
    clearError()
    try {
      await login({ email: values.email, password: values.password })
      executePendingAction()
    } catch (err: unknown) {
      const { message } = handleErrorSilent(err)
      loginForm.setError("password", { message })
    }
  }

  // ── register submit ───────────────────────────────────────────────────
  async function onRegisterSubmit(values: RegisterValues) {
    clearError()
    try {
      const locale = window.location.pathname.split("/")[1] ?? "en"
      await register({
        name: values.name,
        email: values.email,
        password: values.password,
        phone: values.phone || undefined,
        preferred_language: locale === "ar" ? "ar" : "en",
      })
      // Registration succeeded → send OTP and show OTP panel
      openOtpPanel(values.email, "register")
    } catch (err: unknown) {
      const errorResult = handleErrorSilent(err)
      const fieldErrs = getFieldErrors(err)
      if (fieldErrs && Object.keys(fieldErrs).length > 0) {
        Object.entries(fieldErrs).forEach(([k, v]) =>
          registerForm.setError(k as keyof RegisterValues, { message: v })
        )
        return
      }
      if (
        isErrorCode(err, "AUTH_USER_EXISTS") ||
        isErrorCode(err, "RESOURCE_CONFLICT")
      ) {
        registerForm.setError("email", { message: errorResult.message })
        return
      }
      if (isErrorCode(err, "VALIDATION_ERROR")) {
        const msg = errorResult.message.toLowerCase()
        if (msg.includes("phone")) {
          registerForm.setError("phone", { message: errorResult.message })
        } else if (msg.includes("email")) {
          registerForm.setError("email", { message: errorResult.message })
        } else if (msg.includes("password")) {
          registerForm.setError("password", { message: errorResult.message })
        } else {
          registerForm.setError("email", { message: errorResult.message })
        }
        return
      }
      registerForm.setError("email", { message: errorResult.message })
    }
  }

  const isLoginSubmitting = loginForm.formState.isSubmitting
  const isRegisterSubmitting = registerForm.formState.isSubmitting

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-md"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          const first = (
            e.currentTarget as HTMLElement
          ).querySelector<HTMLInputElement>("input")
          first?.focus()
        }}
      >
        <DialogTitle className="sr-only">
          {showOtp
            ? "Verify Email"
            : mode === "forgot-password"
              ? "Forgot Password"
              : mode === "login"
                ? "Login"
                : "Register"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {showOtp
            ? "Enter the 6-digit code sent to your email."
            : mode === "forgot-password"
              ? "Request a password reset link."
              : mode === "login"
                ? "Sign in to your account."
                : "Create a new account."}
        </DialogDescription>
        <div>
          {showOtp ? (
            <div className="space-y-6">
              <div className="space-y-1">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground">
                  Verify your email
                </h1>
                <p className="text-sm text-muted-foreground">
                  A 6-digit code was sent to{" "}
                  <span className="font-medium text-foreground">
                    {otpEmail}
                  </span>
                </p>
              </div>

              <div className="space-y-4">
                <OtpInput
                  value={otpValue}
                  onChange={(v) => {
                    setOtpValue(v)
                    setOtpError("")
                  }}
                  disabled={isVerifyingOtp}
                />

                {otpError && <p className="text-sm text-red-600">{otpError}</p>}

                <Button
                  type="button"
                  size="lg"
                  disabled={
                    isVerifyingOtp || otpValue.replace(/\D/g, "").length < 6
                  }
                  onClick={handleVerifyOtp}
                  className="h-11 w-full cursor-pointer rounded-xl bg-nova-600 font-semibold text-white hover:bg-nova-700 disabled:opacity-50"
                >
                  {isVerifyingOtp ? (
                    <Loader2 size={16} className="me-2 animate-spin" />
                  ) : null}
                  Verify Email
                </Button>

                <div className="flex items-center justify-between gap-3 pt-1">
                  <button
                    type="button"
                    onClick={async () => {
                      await handleSendOtp(otpEmail)
                    }}
                    disabled={isSendingOtp}
                    className="flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSendingOtp ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <RefreshCw size={13} />
                    )}
                    Resend OTP
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      closeOtpPanel()
                      if (otpOrigin === "login") {
                        loginForm.setValue("email", "")
                      } else {
                        registerForm.setValue("email", "")
                      }
                    }}
                    className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                  >
                    Change email
                  </button>
                </div>
              </div>
            </div>
          ) : mode === "login" ? (
            /* ═══════════════════════ LOGIN FORM ═══════════════════════ */
            <div className="space-y-6">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">
                  {t("loginTitle")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("loginSubtitle")}
                </p>
              </div>

              <FormProvider {...loginForm}>
                <form
                  onSubmit={loginForm.handleSubmit(onLoginSubmit)}
                  noValidate
                  className="space-y-4"
                >
                  <TextField
                    name="email"
                    label={t("email")}
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />

                  <PasswordField
                    name="password"
                    label={t("password")}
                    autoComplete="current-password"
                  />

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setForgotPasswordSent(false)
                        setForgotPasswordError("")
                        forgotPasswordForm.reset()
                        switchMode("forgot-password")
                      }}
                      className="text-sm font-medium text-primary underline-offset-4 hover:underline hover:opacity-80"
                    >
                      {t("forgotPassword")}
                    </button>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isLoginSubmitting}
                    className="h-11 w-full cursor-pointer rounded-xl bg-nova-600 font-semibold text-white hover:bg-nova-700"
                  >
                    {isLoginSubmitting ? (
                      <Loader2 size={16} className="me-2 animate-spin" />
                    ) : null}
                    {tNav("login")}
                  </Button>
                </form>
              </FormProvider>

              <p className="text-center text-sm text-muted-foreground">
                {t("noAccount")}{" "}
                <button
                  onClick={() => switchMode("register")}
                  className="cursor-pointer font-medium text-primary underline-offset-4 hover:underline hover:opacity-80"
                >
                  {tNav("register")}
                </button>
              </p>
            </div>
          ) : mode === "forgot-password" ? (
            /* ═══════════════════ FORGOT PASSWORD ═══════════════════ */
            <div className="space-y-6">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">
                  {t("forgotPasswordTitle")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("forgotPasswordSubtitle")}
                </p>
              </div>

              {forgotPasswordSent ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-green-600" />
                    <p className="text-sm text-green-800 dark:text-green-200">
                      {t("resetLinkSent")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setForgotPasswordSent(false)
                      setForgotPasswordError("")
                      forgotPasswordForm.reset()
                      switchMode("login")
                    }}
                    className="flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline hover:opacity-80"
                  >
                    <ArrowLeft size={14} />
                    {t("backToLogin")}
                  </button>
                </div>
              ) : (
                <FormProvider {...forgotPasswordForm}>
                  <form
                    onSubmit={forgotPasswordForm.handleSubmit(
                      onForgotPasswordSubmit
                    )}
                    noValidate
                    className="space-y-4"
                  >
                    <TextField
                      name="email"
                      label={t("email")}
                      type="email"
                      placeholder="you@example.com"
                      autoComplete="email"
                    />

                    {forgotPasswordError && (
                      <p className="text-sm text-red-600">
                        {forgotPasswordError}
                      </p>
                    )}

                    <Button
                      type="submit"
                      size="lg"
                      disabled={forgotPasswordForm.formState.isSubmitting}
                      className="h-11 w-full cursor-pointer rounded-xl bg-nova-600 font-semibold text-white hover:bg-nova-700"
                    >
                      {forgotPasswordForm.formState.isSubmitting ? (
                        <Loader2 size={16} className="me-2 animate-spin" />
                      ) : null}
                      {t("sendResetLink")}
                    </Button>

                    <button
                      type="button"
                      onClick={() => {
                        setForgotPasswordError("")
                        forgotPasswordForm.reset()
                        switchMode("login")
                      }}
                      className="flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline hover:opacity-80"
                    >
                      <ArrowLeft size={14} />
                      {t("backToLogin")}
                    </button>
                  </form>
                </FormProvider>
              )}
            </div>
          ) : (
            /* ════════════════════ REGISTER FORM ════════════════════ */
            <div className="space-y-6">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-foreground">
                  {t("registerTitle")}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t("hasAccount")}{" "}
                  <button
                    onClick={() => switchMode("login")}
                    className="font-medium text-primary underline-offset-4 hover:underline hover:opacity-80"
                  >
                    {tNav("login")}
                  </button>
                </p>
              </div>

              <FormProvider {...registerForm}>
                <form
                  onSubmit={registerForm.handleSubmit(onRegisterSubmit)}
                  noValidate
                  className="space-y-4"
                >
                  <TextField
                    name="name"
                    label={t("name")}
                    type="text"
                    placeholder="John Doe"
                    autoComplete="name"
                  />

                  <TextField
                    name="email"
                    label={t("email")}
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />

                  <PhoneField
                    name="phone"
                    label={t("phone")}
                    optionalLabel={t("optional")}
                  />

                  <PasswordField
                    name="password"
                    label={t("password")}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                  />

                  <PasswordField
                    name="confirmPassword"
                    label={t("confirmPassword")}
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isRegisterSubmitting}
                    className="h-11 w-full cursor-pointer rounded-xl bg-nova-600 font-semibold text-white hover:bg-nova-700"
                  >
                    {isRegisterSubmitting ? (
                      <Loader2 size={16} className="me-2 animate-spin" />
                    ) : null}
                    {tNav("register")}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    {t("verifyEmailNotice")}
                  </p>
                </form>
              </FormProvider>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
